/** Estado das etiquetas (SPEC-OPP-050).
 *
 * A lista é do tenant inteiro e muda pouco: `staleTime` longo, e quem a
 * invalida são as próprias escritas. Marcar um card **também invalida o
 * quadro** — o chip só aparece depois que a projeção alcança, e sem isso a
 * pessoa marca e não vê nada acontecer.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { CHAVE_DO_QUADRO } from "@/features/board/useQuadro";
import { ApiError } from "@/lib/http";

import {
  apagarEtiqueta,
  atualizarEtiqueta,
  criarEtiqueta,
  desmarcar,
  listarEtiquetas,
  marcar,
  type CorDaEtiqueta,
  type Etiqueta,
} from "./api";

export const CHAVE_DAS_ETIQUETAS = ["etiquetas"] as const;

/** Cinco minutos. Vocabulário de empresa não muda entre duas aberturas da
 * mesma tela, e quem o altera invalida a chave aqui mesmo. */
export const VALIDADE_MS = 5 * 60_000;

export function useEtiquetas(): UseQueryResult<Etiqueta[]> {
  return useQuery({
    queryKey: CHAVE_DAS_ETIQUETAS,
    queryFn: listarEtiquetas,
    staleTime: VALIDADE_MS,
  });
}

export function mensagemDoErro(falha: unknown, padrao: string): string {
  return falha instanceof ApiError ? falha.message : padrao;
}

/** As escritas do vocabulário. Uma só invalidação para as três: a lista é
 * pequena e reler inteiro é mais barato que remendar o cache em três lugares
 * e errar num deles. */
export function useGestaoDeEtiquetas() {
  const cliente = useQueryClient();
  const invalidar = async () => {
    await cliente.invalidateQueries({ queryKey: CHAVE_DAS_ETIQUETAS });
    // O nome vive desnormalizado no card: renomear ou apagar muda o quadro.
    await cliente.invalidateQueries({ queryKey: CHAVE_DO_QUADRO });
  };

  const criar = useMutation({
    mutationFn: ({ nome, cor }: { nome: string; cor: CorDaEtiqueta }) => criarEtiqueta(nome, cor),
    onSuccess: invalidar,
  });

  const atualizar = useMutation({
    mutationFn: ({
      id,
      mudancas,
    }: {
      id: string;
      mudancas: { name?: string; color?: CorDaEtiqueta };
    }) => atualizarEtiqueta(id, mudancas),
    onSuccess: invalidar,
  });

  const apagar = useMutation({
    mutationFn: (id: string) => apagarEtiqueta(id),
    onSuccess: invalidar,
  });

  return { criar, atualizar, apagar };
}

/** Marcar e desmarcar um card. */
export function useMarcacao(oportunidade: string) {
  const cliente = useQueryClient();
  const invalidar = async () => {
    await cliente.invalidateQueries({ queryKey: CHAVE_DO_QUADRO });
    await cliente.invalidateQueries({ queryKey: ["oportunidade", oportunidade] });
  };

  const aplicar = useMutation({
    mutationFn: ({ etiqueta, marcada }: { etiqueta: string; marcada: boolean }) =>
      marcada ? desmarcar(oportunidade, etiqueta) : marcar(oportunidade, etiqueta),
    onSuccess: invalidar,
  });

  return aplicar;
}
